/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import {
  CircleCheck,
  CircleX,
  FolderKanban,
  Info,
  KeyRound,
  LoaderCircle,
  Trash2,
} from "lucide-react";

import CopyMultilineValue from "@src/components/ui/CopyMultilineValue";
import CopyValue from "@src/components/ui/CopyValue";
import {
  ActionButton,
  ActionGroup,
  CardList,
  CardListRow,
  DetailsActions,
  DetailsColumns,
  DetailsPage,
  KeyValueGrid,
  SectionCard,
} from "@src/components/ui/DetailsKit";
import IconTile from "@src/components/ui/IconTile";
import { Mono, Pill, T9 } from "@src/components/ui/Lists/ListTable";

import type { User } from "@src/@types/User";
import type { Project } from "@src/@types/Project";

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 160px;
  color: ${T9.slate500};
  font-size: 14px;
  svg {
    color: ${T9.slate400};
    animation: udc-spin 1s linear infinite;
  }
  @keyframes udc-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const ValueLink = styled(Link)`
  color: ${T9.brand600};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
const RowLink = styled(Link)`
  color: ${T9.slate900};
  font-weight: 500;
  text-decoration: none;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  &:hover {
    color: ${T9.brand600};
    text-decoration: underline;
  }
`;
const RowText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Empty = styled.div`
  font-size: 14px;
  color: ${T9.slate500};
  padding: 4px 0 8px 0;
`;

export type Props = {
  user: User | null;
  loading: boolean;
  projects: Project[];
  userProjects: Project[];
  isLoggedUser: boolean;
  onUpdatePasswordClick: () => void;
  onDeleteClick: () => void;
};

@observer
class UserDetailsContent extends React.Component<Props> {
  renderLoading() {
    if (!this.props.loading) {
      return null;
    }

    return (
      <SectionCard testId="userDetails-loading">
        <Loading>
          <LoaderCircle size={20} />
          Loading the user…
        </Loading>
      </SectionCard>
    );
  }

  renderButtons() {
    if (this.props.loading) return null;

    return (
      <DetailsActions>
        <ActionGroup>
          <ActionButton variant="secondary" onClick={this.props.onUpdatePasswordClick}>
            <KeyRound size={16} />
            Change password
          </ActionButton>
        </ActionGroup>
        <ActionButton
          variant="danger"
          onClick={() => {
            this.props.onDeleteClick();
          }}
          disabled={this.props.isLoggedUser}
          title={this.props.isLoggedUser ? "You cannot delete the user you are signed in as" : undefined}
        >
          <Trash2 size={16} />
          Delete user
        </ActionButton>
      </DetailsActions>
    );
  }

  renderStatus(user: User) {
    return user.enabled ? (
      <Pill tone="success" icon={CircleCheck}>
        Enabled
      </Pill>
    ) : (
      <Pill tone="neutral" icon={CircleX}>
        Disabled
      </Pill>
    );
  }

  renderUserProjects(
    projects: { label: string; id: string }[],
    primaryId?: string,
  ): React.ReactNode {
    if (!projects.length) {
      return <Empty>This user is not a member of any project.</Empty>;
    }
    return (
      <CardList>
        {projects.map(project => (
          <CardListRow key={project.id} data-test-id="userDetails-project">
            <IconTile icon={FolderKanban} size={24} />
            {project.label ? (
              <RowLink to={`/projects/${project.id}`} title={project.label}>
                {project.label}
              </RowLink>
            ) : (
              <RowText>
                <Mono>{project.id}</Mono>
              </RowText>
            )}
            {project.id === primaryId ? <Pill tone="brand">Primary</Pill> : null}
          </CardListRow>
        ))}
      </CardList>
    );
  }

  renderInfo() {
    if (this.props.loading || !this.props.user) {
      return null;
    }

    const { user } = this.props;
    const primaryProject = this.props.projects.find(
      p => user.project_id === p.id,
    );
    const userProjects: { label: string; id: string }[] =
      this.props.userProjects.map(up => {
        const projectInfo = this.props.projects.find(p => p.id === up.id);
        if (projectInfo) {
          return { label: projectInfo.name, id: up.id };
        }
        return { label: "", id: up.id };
      });

    return (
      <DetailsColumns columns="minmax(0, 1.6fr) minmax(0, 1fr)">
        <SectionCard
          title="Overview"
          icon={Info}
          testId="userDetails-overview"
        >
          <KeyValueGrid
            columns={2}
            items={[
              { label: "Name", value: user.name ? this.renderValue(user.name) : null },
              { label: "Email", value: user.email ? this.renderValue(user.email) : null },
              {
                label: "Primary project",
                value: primaryProject ? (
                  <ValueLink to={`/projects/${primaryProject.id}`}>{primaryProject.name}</ValueLink>
                ) : (
                  user.project_id || null
                ),
              },
              { label: "Status", value: this.renderStatus(user) },
              { label: "ID", value: this.renderValue(user.id), mono: true, span: 2 },
              {
                label: "Description",
                span: 2,
                value: user.description ? <CopyMultilineValue value={user.description} /> : null,
              },
            ]}
          />
        </SectionCard>
        <SectionCard
          title={`Project membership (${userProjects.length})`}
          icon={FolderKanban}
          testId="userDetails-projects"
        >
          {this.renderUserProjects(userProjects, user.project_id)}
        </SectionCard>
      </DetailsColumns>
    );
  }

  renderValue(value: string) {
    return <CopyValue value={value} maxWidth="100%" />;
  }

  render() {
    return (
      <DetailsPage>
        {this.renderInfo()}
        {this.renderLoading()}
        {this.renderButtons()}
      </DetailsPage>
    );
  }
}

export default UserDetailsContent;
